import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BleReaderButton } from "@/components/BleReaderButton";
import { factoryReset, runDiagnostics, testContinuous, getBleState } from "@/lib/bleReader";

/**
 * Diagnostic page for the R2 Bluetooth reader — shows the RAW bytes the
 * device sends plus every parsed EPC, so frame-format issues can be
 * debugged from a screenshot (no dev console needed on iOS/Bluefy).
 */
export default function BleDebug() {
  const [raw, setRaw] = useState([]);
  const [epcs, setEpcs] = useState([]);
  const [live, setLive] = useState({ pending: 0, sessionTotal: 0, scanning: false });
  const endRef = useRef(null);

  useEffect(() => {
    const onRaw = (e) => setRaw((r) => [...r.slice(-80), e.detail.hex]);
    const onScan = (e) => setEpcs((p) => [...p, ...(e.detail?.epcs || [])]);
    const onStatus = (e) => setLive({ pending: e.detail.pending, sessionTotal: e.detail.sessionTotal, scanning: e.detail.scanning });
    window.addEventListener("ble-reader-raw", onRaw);
    window.addEventListener("pda-rfid-scan", onScan);
    window.addEventListener("ble-reader-status", onStatus);
    return () => {
      window.removeEventListener("ble-reader-raw", onRaw);
      window.removeEventListener("pda-rfid-scan", onScan);
      window.removeEventListener("ble-reader-status", onStatus);
    };
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [raw]);

  return (
    <div className="min-h-screen bg-neutral-900 text-white p-3 space-y-3" data-testid="ble-debug-page">
      <div className="flex items-center gap-3">
        <h1 className="font-display font-bold text-lg flex-1">BLE Reader Debug</h1>
        <BleReaderButton />
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-400 mb-1">
          Parsed EPCs ({epcs.length}) · live: {live.sessionTotal} read{live.scanning ? " — SCANNING" : ""}
        </div>
        <div className="bg-emerald-950/60 border border-emerald-800 rounded-md p-2 min-h-[60px] max-h-40 overflow-y-auto font-mono text-xs text-emerald-300 break-all">
          {epcs.length === 0 ? <span className="text-neutral-500">none yet — connect, press SCAN, hold a tag near the antenna</span>
            : epcs.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-400 mb-1">
          Raw bytes from device (last 80 packets)
        </div>
        <div className="bg-neutral-800 rounded-md p-2 h-[50vh] overflow-y-auto font-mono text-[10px] leading-relaxed text-sky-300 break-all">
          {raw.length === 0 ? <span className="text-neutral-500">nothing received yet</span>
            : raw.map((h, i) => <div key={i}>{h}</div>)}
          <div ref={endRef} />
        </div>
      </div>

      <button
        onClick={() => { setRaw([]); setEpcs([]); }}
        className="w-full h-11 rounded-md bg-neutral-700 hover:bg-neutral-600 text-sm font-semibold"
        data-testid="ble-debug-clear"
      >
        Clear
      </button>
      <button
        onClick={() => {
          if (!getBleState().connected) { toast.error("Connect the reader first"); return; }
          toast.info("Testing continuous mode — keep tags near the antenna (~20s)");
          testContinuous();
        }}
        className="w-full h-11 rounded-md bg-violet-700 hover:bg-violet-600 text-sm font-semibold"
        data-testid="ble-debug-test-continuous"
      >
        Test Continuous Mode (keep tags near, ~20s)
      </button>
      <button
        onClick={() => {
          if (!getBleState().connected) { toast.error("Connect the reader first"); return; }
          toast.info("Running diagnostics — hold a tag against the antenna until done (~5s)");
          runDiagnostics();
        }}
        className="w-full h-11 rounded-md bg-sky-700 hover:bg-sky-600 text-sm font-semibold"
        data-testid="ble-debug-diagnostics"
      >
        Run Diagnostics (hold a tag near the antenna)
      </button>
      <button
        onClick={() => {
          if (!getBleState().connected) { toast.error("Connect the reader first"); return; }
          factoryReset();
          toast.success("Factory reset sent — device will reboot, reconnect afterwards");
        }}
        className="w-full h-11 rounded-md bg-amber-700 hover:bg-amber-600 text-sm font-semibold"
        data-testid="ble-debug-factory-reset"
      >
        Factory Reset (clears filters, device reboots)
      </button>
      <p className="text-[11px] text-neutral-500">
        If EPCs stay empty while raw bytes appear: screenshot this page and send it to the developer —
        the frame format will be adjusted from the raw dump.
      </p>
    </div>
  );
}
